// The MIT License (MIT)
//
// Copyright (c) 2016-2022 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import angular from 'angular';
import {QueryWindowController} from 'gmf/query/windowComponent.js';
import gmfQueryWindow from 'gmf/query/windowComponent.js';

/**
 * @type {angular.IModule}
 * @hidden
 */
const myModule = angular.module('ApronaQueryWindowComponent', [gmfQueryWindow.name]);

myModule.run(
  /**
   * @ngInject
   * @param {angular.ITemplateCacheService} $templateCache
   */
  ($templateCache) => {
    // @ts-ignore: webpack
    $templateCache.put('aprona/displaywindow', require('./windowComponent.html'));
  }
);

class ApronaWindowController extends QueryWindowController {
  /**
   * @param {JQuery} $element Element.
   * @param {angular.IScope} $scope Angular scope.
   * @param {import('ngeo/query/MapQuerent').QueryResult} ngeoQueryResult ngeo query result.
   * @param {import('ngeo/query/MapQuerent').MapQuerent} ngeoMapQuerent ngeo map querent service.
   * @param {import('gmf/options').gmfCsvFilename} gmfCsvFilename The CSV file name.
   * @param {import('gmf/options').gmfDisplayQueryWindowOptions} gmfDisplayQueryWindowOptions The options.$injector
   * @param {angular.$injector} $injector Main injector.
   * @class
   * @hidden
   * @ngInject
   * @ngdoc controller
   * @ngname ApronaWindowController
   */
  constructor(
    $element,
    $scope,
    ngeoQueryResult,
    ngeoMapQuerent,
    gmfCsvFilename,
    gmfDisplayQueryWindowOptions,
    $injector
  ) {
    super($element, $scope, ngeoQueryResult, ngeoMapQuerent, gmfCsvFilename, gmfDisplayQueryWindowOptions);

    this.themes = [
      {
        'name': 'esu-debit',
        'layers': ['stations_hydro'],
      },
      {
        'name': 'esu-crues',
        'layers': [],
      },
      {
        'name': 'esu-qualite',
        'layers': ['rid_67', 'qualitometres_esu'],
      },
      {
        'name': 'eso-niveau',
        'layers': [
          'reseau_piezo_bw',
          'reseau_rcs_quantite',
          'reseau_pts_arretes',
          'reseau_temperature',
          'reseau_centrales_mesure',
          'reseau_aprona',
          'piezometres',
          'piezo_chroniques',
        ],
      },
      {
        'name': 'eso-qualite',
        'layers': [
          'reseau_rcs',
          'reseau_directives_nitrates',
          'reseau_inv_sundgau_1998',
          'reseau_inv_sundgau_1999',
          'reseau_inv_sundgau_2003',
          'reseau_inv_sundgau_2010',
          'reseau_inv_plaine_1997',
          'reseau_inv_plaine_2003',
          'reseau_inv_plaine_2009',
          'qualito_chroniques',
          'qualitometres_eso',
        ],
      },
      {
        'name': 'eso-prelevements',
        'layers': [],
      },
    ];

    /**
     * @type {angular.$injector}
     * @private
     */
    this.$injector_ = $injector;
  }

  /**
   * Return a list of the currently selected geometries.
   * @returns {Array} featuresArray.
   */
  listCurrentGeometries() {
    const sources = this.ngeoQueryResult.sources;
    const featuresArray = [];
    for (let i = 0; i < sources.length; i++) {
      const source = sources[i];
      const features = source.features;
      for (let ii = 0; ii < features.length; ii++) {
        featuresArray.push(features[ii]);
      }
    }
    return featuresArray;
  }

  /**
   * Return if there is a special feature or not.
   * @returns {boolean} return.
   */
  hasSpecialFeature() {
    let founded = false;
    this.records = this.listCurrentGeometries();

    this.records.forEach((record) => {
      this.themes.forEach((theme) => {
        if (theme['layers'].indexOf(record.get('ngeo_feature_type_')) >= 0) {
          founded = true;
          return false;
        }
      });
      if (founded) {
        return false;
      }
    });
    return founded;
  }

  /**
   * Open a new measure page and pre-fill the form with the selected geometries.
   * @returns {boolean}
   */
  openSearch() {
    const foundedThemes = [];
    const codesbss = [];
    let codebss = null;

    this.records.forEach((record) => {
      this.themes.forEach((theme) => {
        if (theme['layers'].indexOf(record.get('ngeo_feature_type_')) >= 0) {
          if (foundedThemes.indexOf(theme['name']) === -1) {
            foundedThemes.push(theme['name']);
          }

          codebss = null;
          switch (theme['name']) {
            case 'eso-qualite':
            case 'eso-niveau':
              codebss = record.get('codebss') || record.get('gw_nummer');
              break;
            case 'esu-debit':
              codebss = record.get('codehydro');
              break;
            case 'esu-qualite':
              codebss = record.get('codestation');
              break;
            default:
              console.error(`${theme['name']} is not supported.`);
          }
          if (codebss !== null) {
            codebss = codebss.substr(0, 10);
            if (codesbss.indexOf(codebss) === -1) {
              codesbss.push(codebss);
            }
          }
        }
      });
    });

    if (foundedThemes.length == 0) {
      return;
    }

    let html = '';
    if (foundedThemes.length === 1) {
      html += '<input type="hidden" id="theme" name="theme" value="' + foundedThemes + '" />';
    }
    html += '<input type="hidden" id="codesbss" name="codesbss" value="' + codesbss + '" />';

    $('<form>', {
      'action': this.$injector_.get('measureSearchUrl') + '#search_results',
      'method': 'POST',
      'target': '_blank',
      'html': html,
    })
      .appendTo(document.body)
      .submit()
      .remove();

    return false;
  }
}

myModule.controller('GmfDisplayquerywindowController', ApronaWindowController);

export default myModule;
